import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useApp } from '@/contexts/AppContext';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const PAIN_LABEL = {
  iam:         'IAM governance',
  containers:  'container governance',
  audit:       'audit evidence',
  incidents:   'incident reconstruction',
  compliance:  'compliance proof',
  board:       'board reporting',
};

const PAIN_PRIORITY = {
  iam:         'Identity Proof Engine',
  containers:  'Container Governance module',
  audit:       'Evidence Vault and Audit Workspace',
  incidents:   'Regulatory Time Travel and Replay',
  compliance:  'framework coverage and evidence packets',
  board:       'Board Reports and Governance Debt Ledger',
};

export default function DesignPartnerModal() {
  const { partnerModalOpen, setPartnerModalOpen } = useApp();
  const [form, setForm] = useState({
    name: '', email: '', company: '',
    account_count: '', pain: '', timeline: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        _subject: `APEX Design Partner Request: ${form.company || 'Unknown'}`,
        source: 'apex-dashboard-design-partner-modal',
      };
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 12000);
      const res = await fetch('https://formspree.io/f/xnjwwanb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(tid);
      if (!res.ok) throw new Error('endpoint_error');
      setDone(true);
      toast.success('Request received', { description: "We'll be in touch within 48 hours." });
    } catch (_) {
      toast.error('Could not submit. Please email hello@apexverity.io directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const onClose = (open) => {
    setPartnerModalOpen(open);
    if (!open) {
      setTimeout(() => {
        setForm({ name: '', email: '', company: '', account_count: '', pain: '', timeline: '' });
        setDone(false);
      }, 250);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={partnerModalOpen} onOpenChange={onClose}>
      <DialogContent data-testid="design-partner-modal" className="sm:max-w-[480px] bg-white max-h-[90vh] overflow-y-auto">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-tight text-apex-ink">Join the APEX Lens Design Partner Program</DialogTitle>
              <DialogDescription className="text-sm text-apex-neutral">
                Work directly with our team to shape the platform. We'll be in touch within 48 hours.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="dp-name" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Name</Label>
                <Input id="dp-name" data-testid="dp-name" value={form.name} onChange={set('name')} placeholder="Jane Doe" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dp-email" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Work Email</Label>
                <Input id="dp-email" data-testid="dp-email" type="email" value={form.email} onChange={set('email')} placeholder="jane@company.com" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dp-company" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Company (optional)</Label>
                <Input id="dp-company" data-testid="dp-company" value={form.company} onChange={set('company')} placeholder="Acme Bank" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dp-account-count" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">AWS accounts (approximate)</Label>
                <select
                  id="dp-account-count"
                  value={form.account_count}
                  onChange={set('account_count')}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-apex-lens/40 bg-white"
                  data-testid="modal-account-count"
                >
                  <option value="">Select range</option>
                  <option value="1-10">1–10 accounts</option>
                  <option value="11-50">11–50 accounts</option>
                  <option value="51-200">51–200 accounts</option>
                  <option value="200+">200+ accounts</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dp-pain" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Biggest governance challenge</Label>
                <select
                  id="dp-pain"
                  value={form.pain}
                  onChange={set('pain')}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-apex-lens/40 bg-white"
                  data-testid="modal-pain"
                >
                  <option value="">Select primary pain</option>
                  <option value="iam">IAM overpermissioning and access reviews</option>
                  <option value="containers">Container governance and finding overload</option>
                  <option value="audit">Audit evidence assembly</option>
                  <option value="incidents">Incident reconstruction and investigation</option>
                  <option value="compliance">SOC 2 / ISO 27001 / DORA compliance proof</option>
                  <option value="board">Board-level governance reporting</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dp-timeline" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Pilot timeline</Label>
                <select
                  id="dp-timeline"
                  value={form.timeline}
                  onChange={set('timeline')}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-apex-lens/40 bg-white"
                  data-testid="modal-timeline"
                >
                  <option value="">Select timeline</option>
                  <option value="immediate">Ready to start immediately</option>
                  <option value="30days">Within 30 days</option>
                  <option value="quarter">This quarter</option>
                  <option value="exploring">Exploring — no timeline yet</option>
                </select>
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => onClose(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="dp-submit" className="bg-apex-lens hover:bg-apex-lens-dark text-white">
                  {submitting ? 'Sending…' : 'Request Access'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-8 h-8 text-apex-success" strokeWidth={2} />
            </div>
            <h3 className="text-lg font-bold text-apex-ink">You're on the list</h3>
            <p className="text-sm text-apex-neutral mt-1 max-w-xs mx-auto">We'll reach out to {form.email} within 48 hours.</p>
            {form.pain && (
              <p className="text-[12px] text-apex-neutral mt-3 max-w-sm mx-auto leading-relaxed" data-testid="dp-personalized">
                Based on your focus on{' '}
                <span className="font-semibold text-apex-ink">{PAIN_LABEL[form.pain]}</span>,
                we'll prioritize the{' '}
                <span className="font-semibold text-apex-lens">{PAIN_PRIORITY[form.pain]}</span>{' '}
                in your pilot scope.
              </p>
            )}

            {/* Timeline-specific next step */}
            {form.timeline && (
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 text-left mt-4">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-apex-neutral mb-2">
                  Your next step
                </div>
                <p className="text-[13px] text-apex-ink leading-relaxed" data-testid="dp-next-step">
                  {form.timeline === 'immediate'
                    ? 'We will reach out within 24 hours to schedule your architecture review call. Please have CloudTrail enabled and read-only IAM access ready.'
                    : form.timeline === '30days'
                    ? 'We will contact you within 48 hours to begin pilot scoping. We will need to verify CloudTrail retention and confirm your AWS account structure.'
                    : form.timeline === 'quarter'
                    ? 'We will send you a pilot overview document and schedule a scoping call at your convenience.'
                    : 'We will add you to our design partner waitlist and reach out when pilot slots open.'}
                </p>
              </div>
            )}

            {/* What to have ready — only for near-term pilots */}
            {(form.timeline === 'immediate' || form.timeline === '30days') && (
              <div className="text-left mt-3" data-testid="dp-checklist">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-apex-neutral mb-2">
                  What to have ready
                </div>
                <ul className="space-y-1">
                  {[
                    'CloudTrail enabled with at least 90 days of history',
                    'Read-only IAM role or AWS Organizations read access',
                    'List of AWS accounts in scope for the pilot',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px] text-apex-neutral">
                      <span className="text-apex-lens mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={() => onClose(false)} className="mt-5 bg-apex-lens hover:bg-apex-lens-dark text-white">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
